import { Environment, XID } from "@atlassian/atl-cross-domain-tracking";
import AnalyticsWebClient, {
    envType,
    userType
} from "@atlassiansox/analytics-web-client";

const getAnonIdUsedByFeatureGatesClientInSSR = (): string =>
    window.featureGateBootstrapValues?.identifiers?.analyticsAnonymousId ?? "";

const readCookieValue = (name: string): string | null => {
    const match = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`).exec(
        document.cookie
    );
    return match?.[1] !== undefined ? decodeURIComponent(match[1]) : null;
};

const getAtlassianUserId = () => {
    const userId = readCookieValue("__aid_user_id");
    if (userId) {
        return decodeURIComponent(userId);
    }
    return null;
};

const getGASv3Environment = (): string => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return envType.PROD;
        case "staging":
            return envType.STAGING;
        case "development":
            return envType.DEV;
        default:
            return envType.LOCAL;
    }
};

const getXidEnvironment = (): Environment => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
            return Environment.PROD;
        case "staging":
            return Environment.STAGE;
        case "development":
            return Environment.DEV;
        default:
            return Environment.LOCAL;
    }
};

const shouldUseStargate = (): boolean => {
    switch (process.env["CLIENT_LIBRARY_ENV"]) {
        case "production":
        case "staging":
            return true;
        case "development":
        default:
            return false;
    }
};

const getBuildVersion = () => process.env["VERSION"] || "1.0.0";

const getApiHostProtocol = (): "http" | "https" => {
    if (typeof window !== "undefined" && window.location.protocol === "http:") {
        return "http";
    }
    return "https";
};

export const getAnalyticsWebClientTrello = (): AnalyticsWebClient => {
    const client = new AnalyticsWebClient(
        {
            env: getGASv3Environment(),
            product: "trello",
            version: getBuildVersion(),
            locale: "en-us"
        },
        {
            useStargate: shouldUseStargate(),
            apiHostProtocol: getApiHostProtocol(),
            xidConsent: true,
            xidPromiseFn: () =>
                new XID(getXidEnvironment()).getXidCallbackForPromise(),
            customAnonymousIdGenerator: getAnonIdUsedByFeatureGatesClientInSSR,
            flushWaitInterval: 100
        }
    );

    const aaid = getAtlassianUserId();
    if (aaid) {
        client.setUserInfo(userType.ATLASSIAN_ACCOUNT, aaid);
    }

    return client;
};
